import { useState, useEffect, useCallback } from "react";
import { DatePicker, Select, Table, Checkbox, message, Dropdown } from "antd";
import { Button } from "react-bootstrap";
import { MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { getBills } from "../../services/doctor/bills/bills";

const QUICK_RANGES = ["Today", "1W", "2W", "1M", "3M", "6M", "1Y", "ALL"];

export default function InClinicAccounting() {
    const [startDate, setStartDate] = useState(dayjs("2015-01-01"));
    const [endDate, setEndDate] = useState(dayjs());
    const [activeRange, setActiveRange] = useState("ALL");
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [filterValue, setFilterValue] = useState("ALL");
    const [messageApi, contextHolder] = message.useMessage();

    // Compute start/end date for each quick range button
    const getRangeDates = (rangeKey) => {
        const today = dayjs();
        switch (rangeKey) {
            case "Today":
                return { start: today, end: today };
            case "1W":
                return { start: today.subtract(1, "week"), end: today };
            case "2W":
                return { start: today.subtract(2, "week"), end: today };
            case "1M":
                return { start: today.subtract(1, "month"), end: today };
            case "3M":
                return { start: today.subtract(3, "month"), end: today };
            case "6M":
                return { start: today.subtract(6, "month"), end: today };
            case "1Y":
                return { start: today.subtract(1, "year"), end: today };
            case "ALL":
            default:
                return { start: dayjs("2015-01-01"), end: today };
        }
    };

    const handleQuickRangeClick = (rangeKey) => {
        setActiveRange(rangeKey);
        const { start, end } = getRangeDates(rangeKey);
        setStartDate(start);
        setEndDate(end);
    };

    // Called whenever the manual date pickers change
    const handleStartDateChange = (date) => {
        setActiveRange(null); // manual override, no quick-range highlighted
        setStartDate(date);
    };

    const handleEndDateChange = (date) => {
        setActiveRange(null);
        setEndDate(date);
    };

    const fetchBills = useCallback(async () => {
        if (!startDate || !endDate) return;
        setLoading(true);
        try {

            const response = await getBills("","","","",startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"),filterValue=="all"? "":filterValue);
           

            if (response?.success) {
                setBills(response?.data);
            } else {
                setBills([]);
                messageApi.error(response?.message);
            }
        } catch (err) {
            messageApi.error("Failed to load bills");
            setBills([]);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, messageApi,filterValue]);

    // Refetch whenever the date range changes (quick button or manual pick)
    useEffect(() => {
        fetchBills();
    }, [fetchBills]);

    // ---- Summary calculations ----
    const totalPayable = bills.reduce(
        (sum, b) => sum + Number(b.payment_amount || 0),
        0
    );
    const totalDeposited = bills.reduce(
        (sum, b) => sum + Number(b.amount_paid ?? b.payment_amount ?? 0),
        0
    );
    const calculatedDue = totalPayable - totalDeposited;
    const totalDue = bills.reduce((sum, b) => {
        const diff = Number(b.payment_amount || 0) - Number(b.amount_paid ?? b.payment_amount ?? 0);
        return diff > 0 ? sum + diff : sum;
    }, 0);
    const totalAdvance = bills.reduce((sum, b) => {
        const diff = Number(b.amount_paid ?? b.payment_amount ?? 0) - Number(b.payment_amount || 0);
        return diff > 0 ? sum + diff : sum;
    }, 0);
    const depositedPercentage =
        totalPayable > 0 ? Math.round((totalDeposited / totalPayable) * 100) : 0;

const handleDownload = () => {
  if (!bills || bills.length === 0) {
    messageApi.warning("No data to download");
    return;
  }

  const headers = ["Patient Name", "Date", "Bill No.", "Amount", "receiptNo", "Amount Paid"];

  const escapeCsvValue = (value) => {
    const str = String(value ?? "");
    // Wrap in quotes if it contains a comma, quote, or newline
    if (/[",\n]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = bills.map((bill) => {
    const patientName = bill.patient
      ? [bill.patient.first_name, bill.patient.middle_name, bill.patient.last_name]
          .filter(Boolean)
          .join(" ")
      : bill.patient_name || "-";

    const date = bill.created_at ? dayjs(bill.created_at).format("DD-MM-YYYY") : "-";
    const billNo = bill.doctor_bill_no || "-";
    const amount = bill.payment_amount ?? 0;
    const receiptNo = bill.doctor_receipt_no || "-";
    const amountPaid = bill.amount_paid ?? bill.payment_amount ?? 0;

    return [patientName, date, billNo, amount, receiptNo, amountPaid];
  });

  const csvContent = [headers, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");

  // Add BOM so Excel opens UTF-8 correctly (handles names with special characters)
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "Accounting_summary.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  messageApi.success("Download started");
};

const handlePrintForm3C = () => {
  if (!bills || bills.length === 0) {
    messageApi.warning("No data to print");
    return;
  }

  // Build Form 3C table rows from current bills data
  const tableRows = bills
    .map((bill, index) => {
      const patientName = bill.patient
        ? [bill.patient.first_name, bill.patient.middle_name, bill.patient.last_name]
            .filter(Boolean)
            .join(" ")
        : bill.patient_name || "-";

      const date = bill.created_at ? dayjs(bill.created_at).format("DD/MM/YYYY") : "-";
      const slNo = bill.doctor_receipt_no || "-";

      // "Nature of professional services rendered" — derived from bill items, joined by comma
      const services =
        Array.isArray(bill.items) && bill.items.length > 0
          ? bill.items.map((it) => it.fee_name).filter(Boolean).join(", ")
          : "-";

      const feesReceived = `₹${Number(bill.payment_amount || 0).toLocaleString("en-IN")}`;
      const dateOfReceipt = date;

      return `
        <tr>
          <td>${date}</td>
          <td>${slNo}</td>
          <td>${patientName}</td>
          <td>${services}</td>
          <td style="text-align:right;">${feesReceived}</td>
          <td>${dateOfReceipt}</td>
        </tr>
      `;
    })
    .join("");

  const printContents = `
    <table class="form3c-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Sl. No.</th>
          <th>Patient's name</th>
          <th><em>Nature of professional services rendered, i.e., general consultation, surgery, injection, visit, etc.</em></th>
          <th>Fees received</th>
          <th>Date of receipt</th>
        </tr>
        <tr>
          <th style="text-align:center;">(1)</th>
          <th style="text-align:center;">(2)</th>
          <th style="text-align:center;">(3)</th>
          <th style="text-align:center;">(4)</th>
          <th style="text-align:center;">(5)</th>
          <th style="text-align:center;">(6)</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
  `;

  const printWindow = window.open(
    "about:blank", // THIS FIXES SAFARI
    "printWin",
    "popup=yes,width=1000,height=900"
  );
  printWindow.document.write(`
    <html>
      <head>
        <title>Form 3C</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no" />
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
          crossorigin="anonymous"
        />
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          body, table, div, span, p {
            font-size: 13px !important;
          }

          html, body {
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            background: white;
            overflow: hidden;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .print-wrapper {
            width: 100%;
            margin: 0 auto;
            box-sizing: border-box;
            padding: 10px;
            background: white;
            transform-origin: top center;
          }

          table.form3c-table {
            width: 100%;
            border-collapse: collapse;
          }

          .form3c-table th,
          .form3c-table td {
            border: 1px solid #333;
            padding: 6px 8px;
            font-size: 13px;
            vertical-align: top;
          }

          .form3c-table thead th {
            background: #fbe4d5;
            text-align: left;
            font-weight: 600;
          }

          .form3c-table tbody tr {
            background: #fff9db;
            color: #1a3d6d;
          }

          .no-print { display: none !important; }
          hr { border: 0; border-top: 1px solid #ccc; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div id="print-area" class="print-wrapper">
          ${printContents}
        </div>
        <script>
          window.onload = function() {
            const content = document.getElementById('print-area');
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

            // Get A4 landscape height in pixels at 96 DPI
            const A4_HEIGHT_PX = 793; // 210mm ≈ 793px
            const contentHeight = content.scrollHeight;

            if (contentHeight > A4_HEIGHT_PX) {
              const scale = A4_HEIGHT_PX / contentHeight;
              content.style.transform = 'scale(' + scale + ')';
              content.style.transformOrigin = 'top center';
              document.body.style.height = A4_HEIGHT_PX + 'px';
            } else if (isIOS) {
              content.style.transform = 'scale(1)';
              content.style.transformOrigin = 'top center';
              document.body.style.margin = '0';
              document.body.style.padding = '0';
              content.style.width = '100vw';
            }

            setTimeout(() => {
              window.focus();
              window.print();
              let attempts = 0;

              function tryClose() {
                try {
                  window.close();

                  if (!window.closed && attempts < 10) {
                    attempts++;
                    setTimeout(tryClose, 300);
                  }
                } catch (e) {
                  if (attempts < 10) {
                    attempts++;
                    setTimeout(tryClose, 300);
                  }
                }
              }

              window.onafterprint = tryClose;

              // failsafe auto close after 1 second
              setTimeout(tryClose, 1000);
            }, 500);
          };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();

      let attempts = 0;
      function tryClose() {
        try {
          printWindow.close();
          if (!printWindow.closed && attempts < 10) {
            attempts++;
            setTimeout(tryClose, 400);
          }
        } catch {
          if (attempts < 10) {
            attempts++;
            setTimeout(tryClose, 400);
          }
        }
      }

      // Safari: onafterprint rarely works, but include it
      printWindow.onafterprint = tryClose;

      // Fallback close
      setTimeout(tryClose, 1500);
    }, 300);
  };
};

    const handleDelete = () => {
        if (selectedRowKeys.length === 0) return;
        // Call your delete API for selectedRowKeys here
        messageApi.success(`${selectedRowKeys.length} bill(s) deleted`);
        setSelectedRowKeys([]);
        fetchBills();
    };

    const handleAddTo3C = () => {
        if (selectedRowKeys.length === 0) return;
        messageApi.success(`${selectedRowKeys.length} bill(s) added to 3C`);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const columns = [
        {
            title: "Patient Name",
            dataIndex: "patient_name",
            key: "patient_name",
            render: (_, record) => {
                const p = record.patient;
                if (!p) return "-";
                return [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(" ");
            },
        },
        {
            title: "Date",
            dataIndex: "created_at",
            key: "date",
            render: (val) => (val ? dayjs(val).format("DD MMM YYYY") : "-"),
        },
        {
            title: "No. of Edits",
            dataIndex: "number_of_edits",
            key: "number_of_edits",
            render: (val) => val ?? 0,
        },
        {
            title: "Bill No.",
            dataIndex: "doctor_bill_no",
            key: "doctor_bill_no",
        },
        {
            title: "Receipt No.",
            dataIndex: "doctor_receipt_no",
            key: "doctor_receipt_no",
        },
        {
            title: "Amount",
            dataIndex: "payment_amount",
            key: "payment_amount",
            render: (val) => `₹${Number(val || 0).toLocaleString("en-IN")}`,
        },
        {
            title: "Amount Paid",
            dataIndex: "amount_paid",
            key: "amount_paid",
            render: (_, record) =>
                `₹${Number(record.amount_paid ?? record.payment_amount ?? 0).toLocaleString("en-IN")}`,
        },
        {
            title: "",
            key: "actions",
            width: 50,
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: [
                            { key: "view", label: "View" },
                            { key: "edit", label: "Edit" },
                            { key: "delete", label: "Delete", danger: true },
                        ],
                    }}
                    trigger={["click"]}
                >
                    <MoreOutlined style={{ cursor: "pointer", fontSize: "18px" }} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div>
            {contextHolder}

            {/* Date range + filter panel */}
            <div
                style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: "6px",
                    padding: "16px",
                    marginBottom: "16px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <Text strongLabel>Date Range:</Text>
                    <DatePicker
                        value={startDate}
                        format="DD-MM-YYYY"
                        onChange={handleStartDateChange}
                    />
                    <DatePicker
                        value={endDate}
                        format="DD-MM-YYYY"
                        onChange={handleEndDateChange}
                    />

                    {QUICK_RANGES.map((range) => (
                        <Button
                            key={range}
                            size="sm"
                            variant={activeRange === range ? "primary" : "outline-secondary"}
                            onClick={() => handleQuickRangeClick(range)}
                            style={{
                                borderRadius: "6px",
                                fontWeight: 500,
                            }}
                        >
                            {range}
                        </Button>
                    ))}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{ color: "#595959" }}>Filters:</span>
                    <Select
                        value={filterValue}
                        onChange={setFilterValue}
                        style={{ width: 160 }}
                        options={[
                            { value: "ALL", label: "All" },
                            { value: "CASH", label: "CASH" },
                            { value: "UPI", label: "UPI" },
                        ]}
                    />
                    <Button variant="outline-secondary" size="sm" onClick={handleDownload}>
                        Download
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={handlePrintForm3C}>
                        Print
                    </Button>
                </div>
            </div>

            {/* Summary cards */}
            <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                <SummaryCard
                    title="Total Payable Amount"
                    value={`₹${totalPayable.toLocaleString("en-IN")}`}
                    subtitle={`Total bills: ${bills.length}`}
                />
                <SummaryCard
                    title="Total Deposited"
                    value={`₹${totalDeposited.toLocaleString("en-IN")}`}
                    subtitle={`Percentage: ${depositedPercentage}%`}
                />
                <SummaryCard
                    title="Calculated Due"
                    value={`₹${calculatedDue.toLocaleString("en-IN")}`}
                    subtitle={`Total Due: ₹${totalDue.toLocaleString("en-IN")}, Total Advance: ₹${totalAdvance.toLocaleString("en-IN")}`}
                />
            </div>

            {/* Selected bar */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                }}
            >
                <span>{selectedRowKeys.length} Bills selected</span>
                {/* <div style={{ display: "flex", gap: "8px" }}>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        disabled={selectedRowKeys.length === 0}
                        onClick={handleDelete}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={selectedRowKeys.length === 0}
                        onClick={handleAddTo3C}
                    >
                        Add to 3C
                    </Button>
                </div> */}
            </div>

            {/* Table */}
            <Table
                rowKey={(record) => record.id}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={bills}
                loading={loading}
                pagination={{ pageSize: 10 }}
            />
        </div>
    );
}

// Small inline helper components
function Text({ children, strongLabel }) {
    return (
        <span style={{ fontWeight: strongLabel ? 500 : 400, color: "#262626" }}>
            {children}
        </span>
    );
}
Text.propTypes = {
    children: PropTypes.node.isRequired,
    strongLabel: PropTypes.bool,
};

Text.defaultProps = {
    strongLabel: false,
};

function SummaryCard({ title, value, subtitle }) {
    return (
        <div
            style={{
                flex: 1,
                border: "1px solid #f0f0f0",
                borderRadius: "6px",
                padding: "20px",
                textAlign: "center",
            }}
        >
            <div style={{ color: "#8c8c8c", marginBottom: "8px" }}>{title}</div>
            <div style={{ fontSize: "28px", fontWeight: 600, marginBottom: "8px" }}>
                {value}
            </div>
            <div style={{ color: "#8c8c8c", fontSize: "13px" }}>{subtitle}</div>
        </div>
    );
}

SummaryCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
};